import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { EnquiryModal } from '../components/forms/EnquiryModal';
export interface EnquiryConfig {
  title: string;
  description: string;
  submitLabel: string;
  defaultCourse?: string;
  defaultLocation?: string;
  showCourseFields?: boolean;
  messageLabel?: string;
}
interface EnquiryModalContextValue {
  open: (config?: Partial<EnquiryConfig>) => void;
  close: () => void;
  setPageConfig: (config: EnquiryConfig | null) => void;
}
const fallbackConfig: EnquiryConfig = {
  title: 'Talk with our team',
  description: 'Tell us what you want to study, when and where. Our admissions team replies with a plan, prices and next steps.',
  submitLabel: 'Send my enquiry'
};
const EnquiryModalContext = createContext<EnquiryModalContextValue | null>(null);

// Holds the site-wide enquiry popup. Any link to "#enquire" opens it.
export function EnquiryModalProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [pageConfig, setPageConfig] = useState<EnquiryConfig | null>(null);
  const [override, setOverride] = useState<Partial<EnquiryConfig>>({});
  const open = useCallback((config: Partial<EnquiryConfig> = {}) => {
    setOverride(config);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
      const anchor = (event.target as HTMLElement | null)?.closest('a');
      const href = anchor?.getAttribute('href');
      if (!href || !(href === '#enquire' || href.endsWith('/#enquire'))) return;
      event.preventDefault();
      event.stopPropagation();
      open();
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [open]);
  const value = useMemo(() => ({
    open,
    close,
    setPageConfig
  }), [open, close]);
  const config = {
    ...fallbackConfig,
    ...(pageConfig ?? {}),
    ...override
  };
  return <EnquiryModalContext.Provider value={value} data-id="element-911" data-mp="ybl3ohyqqiuw">
      {children}
      <EnquiryModal isOpen={isOpen} onClose={close} config={config} data-id="element-912" data-mp="joi4m6urs25g" />
    </EnquiryModalContext.Provider>;
}
export function useEnquiryModal() {
  const ctx = useContext(EnquiryModalContext);
  if (!ctx) throw new Error('useEnquiryModal must be used inside EnquiryModalProvider');
  return ctx;
}